/**
 * <modal-bundle-dialog> + <modal-bundle-opener> — the bundle's flavour dialog
 * and the per-slot opener (P7). Extends Dawn's ModalDialog from global.js.
 *
 * The dialog positions itself under the slot that opened it (PDP), or moves
 * inline after the slot inside the promo popup's <dialog> (where the
 * body-move of ModalDialog.connectedCallback would tear it out of the
 * top-layer context).
 */
class ModalBundleDialog extends ModalDialog {
  constructor() {
    super();
    this.positionModal = this.positionModal.bind(this);
    this.debouncedPositionModal = debounce(this.positionModal, 100);
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    document.addEventListener('modalClosed', this.hide.bind(this));
  }

  connectedCallback() {
    // Skip ModalDialog's body-move when inside a promo popup dialog, otherwise
    // the element leaves the <dialog> top-layer context.
    if (!this.closest('.promo-bundle-popup')) {
      super.connectedCallback();
    }
    this.addEventListener('openModal', this.positionModal);
    window.addEventListener('resize', this.debouncedPositionModal);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.debouncedPositionModal);
  }

  show(opener) {
    if (this.hasAttribute('open')) this.hide();

    this.openedBy = opener;
    this.querySelector('.template-popup')?.loadContent();
    this.style.display = ''; // Clear any inline display:none from a previous close
    this.setAttribute('open', '');
    this.positionModal({ detail: { opener } });

    setTimeout(() => {
      document.body.addEventListener('click', this.handleOutsideClick);
      document.body.addEventListener('keyup', this.handleEscapeKey);
    }, 300);
  }

  hide() {
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    document.body.removeEventListener('click', this.handleOutsideClick);
    document.body.removeEventListener('keyup', this.handleEscapeKey);

    // Move back outside [data-bundle-content] so reset()'s innerHTML
    // replacement cannot destroy the dialog.
    if (this.classList.contains('promo-bundle-popup__flavor-modal')) {
      const bundleWrapper = this.closest('.product-bundle.bundle-modal-version')
        || this.closest('[data-bundle-content]')?.parentElement;
      if (bundleWrapper) {
        bundleWrapper.insertAdjacentElement('afterend', this);
      }
    }
  }

  handleOutsideClick(event) {
    if (!this.contains(event.target) && this.hasAttribute('open')) this.hide();
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.hasAttribute('open')) this.hide();
  }

  positionModal(event) {
    const opener = event?.detail?.opener || this.openedBy;
    if (!opener) {
      console.error('Opener is not defined');
      return;
    }

    const currentData = opener.getAttribute('data-current');
    const parentElement = opener.closest(`.product-bundle__product[data-current="${currentData}"]`);
    if (!parentElement) return;

    this.setCheckedState(parentElement);
    if (this.classList.contains('promo-bundle-popup__flavor-modal')) {
      // Move the dialog to appear right after the clicked bundle item.
      parentElement.insertAdjacentElement('afterend', this);
      requestAnimationFrame(() => {
        this.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      return;
    }
    const targetRect = parentElement.getBoundingClientRect();
    this.style.position = 'absolute';
    this.style.top = `${targetRect.bottom + window.scrollY}px`;
    this.style.left = `${targetRect.left + window.scrollX}px`;
    this.style.width = `${targetRect.width}px`;
  }

  setCheckedState(slotElement) {
    const variantId = slotElement.getAttribute('data-product-variant-id');
    this.querySelectorAll('.product-bundle__product').forEach((product) => product.removeAttribute('data-checked'));
    this.querySelector(`.product-bundle__product[data-id="${variantId}"]`)?.setAttribute('data-checked', '');
  }
}

customElements.define('modal-bundle-dialog', ModalBundleDialog);

class ModalBundleOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');
    if (!button) return;

    const bundleItem = button.closest('.product-bundle__product');
    if (!bundleItem) return;

    bundleItem.addEventListener('click', (event) => {
      event.stopPropagation();
      const dataType = bundleItem.getAttribute('data-type');

      if (dataType === 'default' || dataType === 'selected' || dataType === 'additional') {
        const modalSelector = this.getAttribute('data-modal');
        const modal = document.querySelector(modalSelector);

        if (modal) {
          modal.dataset.currentItem = button.dataset.current;
          modal.show(button);
        }
      }
    });
  }
}

customElements.define('modal-bundle-opener', ModalBundleOpener);
